<template>
  <Modal
    :title="modalTitle"
    :width="1000"
    :submit-btn-text="submitButtonText"
    :no-footer="loading"
    :class-name="'emailQuoteOrInvoiceModal white'"
    :saving="saving"
    @submit="handleSubmit"
    @hide-modal="emit('hide-modal')"
  >
    <div v-if="loading">
      <div>Loading customer details...</div>
    </div>
    <div
      v-else-if="noPrimaryEmailFound"
      class="mb-2"
      data-cy="no-primary-email-found"
    >
      <p class="mb-4">This customer is missing a primary email address.</p>
    </div>
    <div v-else>
      <ErrorDisplay
        v-if="errorMessages.onePrimaryOrAddressRequired"
        class="mb-3"
      >
        <span>{{ errorMessages.onePrimaryOrAddressRequired }}</span>
      </ErrorDisplay>
      <div class="twoColumns">
        <Recipients />
        <AdhocEmailContent />
      </div>
    </div>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from "vue";
import { useStore } from "vuex";

import ErrorDisplay from "@/components/ErrorDisplay.vue";
import Modal from "@/components/Modal.vue";
import { EmailTemplateCustomization } from "@/EmailTemplates/emailTemplate";
import { useEmailTemplates } from "@/EmailTemplates/useEmailTemplates";
import AdhocEmailContent from "./AdhocEmailContent.vue";
import Recipients from "./Recipients/Recipients.vue";
import { EmailQuoteOrInvoiceProps } from "./types";
import { useEmailQuoteOrInvoice } from "./useEmailQuoteOrInvoice";

const props = defineProps({
  quote: {
    type: Object as () => EmailQuoteOrInvoiceProps["quote"],
    default: null
  },
  invoice: {
    type: Object as () => EmailQuoteOrInvoiceProps["invoice"],
    default: null
  }
});
const emit = defineEmits(["hide-modal"]);

const {
  init,
  customerId,
  defaultRecipients,
  errorMessages,
  selectedEmailAddresses,
  validate
} = useEmailQuoteOrInvoice(true);
const { templateCustomizations } = useEmailTemplates(true, {
  previewEmailTemplate,
  viewEmailTemplateNoCustomizations
});
const saving = ref(false);
const store = useStore();
const loading = ref(true);

// In the event we don't find any primary email addresses, we need
// to let the user know they need to set one up
const noPrimaryEmailFound = computed(() => {
  if (loading.value) return false;
  return !defaultRecipients.value.find((contact) => contact.primary);
});

const modalTitle = computed(() => {
  const subject = props.quote ? "quote" : "invoice";
  if (noPrimaryEmailFound.value) return `Unable to send ${subject}`;
  return `Email ${subject} to`;
});

const submitButtonText = computed(() => {
  if (noPrimaryEmailFound.value) return "Edit Customer Information";
  return "Send";
});

onMounted(async () => {
  loading.value = true;
  try {
    await init(props);
  } catch (e) {
    window.notify.error(`Unable to load customer information at this time.`);
    emit("hide-modal");
  }
  loading.value = false;
});

async function previewEmailTemplate(
  customizations: EmailTemplateCustomization[]
) {
  if (props.quote) {
    return store.dispatch("previewQuoteEmailTemplate", {
      quote: props.quote,
      customizations
    });
  }
  return store.dispatch("previewInvoiceEmailTemplate", {
    invoice: props.invoice,
    customizations
  });
}

async function viewEmailTemplateNoCustomizations() {
  if (props.quote) {
    return store.dispatch(
      "viewQuoteEmailTemplateNoCustomizations",
      props.quote
    );
  }
  return store.dispatch(
    "viewInvoiceEmailTemplateNoCustomizations",
    props.invoice
  );
}

async function handleSubmit() {
  if (noPrimaryEmailFound.value) {
    return handleEditCustomerInformation();
  }
  return handleSend();
}

function handleEditCustomerInformation() {
  window.open(`/customers/${customerId.value}?edit_customer`, "_blank");
  emit("hide-modal");
}

async function handleSend() {
  const isValid = await validate();
  if (!isValid) return;
  const email_addresses = [
    ...selectedEmailAddresses.value.default,
    ...selectedEmailAddresses.value.additional
  ];
  saving.value = true;
  try {
    if (props.quote) {
      await store.dispatch("emailQuote", {
        link: props.quote.links!.quote_email,
        email_addresses,
        customizations: templateCustomizations.value
      });
      window.notify.success(`Quote emailed successfully!`);
    } else {
      await store.dispatch("emailInvoice", {
        link: props.invoice!.links!.invoice_email,
        email_addresses,
        customizations: templateCustomizations.value
      });
      window.notify.success(`Invoice emailed successfully!`);
    }
    emit("hide-modal");
  } finally {
    saving.value = false;
  }
}
</script>

<style lang="scss">
.emailQuoteOrInvoiceModal {
  &.white {
    .modal-content {
      background: white;
    }
  }
  .twoColumns {
    display: grid;
    grid-template-columns: calc(400px) calc(100% - 400px - 15px);
    gap: 15px;
  }
}
</style>
